/** @jsx jsx */
import { jsx, css } from "@emotion/core"
import React from "react"
import { Spacer, ConditionalWrapper, ToolTip, Icon } from "@cd2/components"
import { useTheme } from "@cd2/theme"
import { NavLink } from "react-router-dom"
import { IconDefinition } from "@fortawesome/fontawesome-common-types"

const groupSeparator = css`
  width: 3px;
  background: #ddd;
  margin: 0 0 -1px 0;
  flex-shrink: 0;
`

interface IProps {
  tabs: {
    children: React.ReactNode
    icon: string | IconDefinition
    to: string
    disabled?: string | boolean
    exact?: boolean
    count?: number
    color?: string
    group?: string
  }[]
}
export function PageTabs({ tabs }: IProps) {
  const theme = useTheme()
  return (
    <div
      css={css`
        display: flex;
        flex-wrap: wrap;
        padding: 0 0 0;
        border-bottom: 1px solid #ddd;
      `}
    >
      {tabs.map((tab, i) => {
        const isNewGroup = i > 0 && tab.group !== undefined && tabs[i - 1].group !== tab.group
        return (
          <React.Fragment key={i}>
            {isNewGroup && <div css={groupSeparator} />}
            <ConditionalWrapper
              condition={typeof tab.disabled === "string"}
              Wrapper={({ children }) => (
                <ToolTip hoverContent={() => tab.disabled}>{children}</ToolTip>
              )}
            >
              <NavLink
                css={css`
                padding: 10px 12px;
                @media (pointer: coarse) {
                  padding: 11px 12px;
                }

                display: inline-block;
                text-decoration: none;
                color: #333;
                font-weight: 500;
                text-align: center;
                font-size: 13px;
                white-space: nowrap;
                border: 1px solid #ddd;
                background-color: #f5f5f5;
                transition: background-color 0.2s;
                margin: 0 0 -1px -1px;
                position: relative;
                cursor: ${tab.disabled ? "not-allowed" : "pointer"};
                opacity: ${tab.disabled ? 0.5 : 1};
  }
                &.active {
                  &:after {
                    content: "";
                    background: ${tab.color ?? theme.themeColor("primary")};
                    top: 0;
                    width: calc(100% + 2px);
                    z-index: 1;
                    height: 3px;
                    left: 0;
                    margin: -1px;
                    position: absolute;
                  }
                  position: relative;
                  background: white;
                  border-bottom: 1px solid #f5f5f5;
                    &:hover {
                      @media (hover: hover) {
                        background-color: ${tab.disabled ? "" : "white"};
                      }
                    }
                  }
                  &:hover {
                    @media (hover: hover) {
                      background-color: ${tab.disabled ? "" : "#f9f9f9"};
                    }
                  }
                `}
                to={tab.to}
                exact={tab.exact ?? true}
                onClick={e => {
                  if (tab.disabled) {
                    e.preventDefault()
                  }
                }}
              >
                <Icon icon={tab.icon} color="#999" /> <Spacer size={4} horizontal />
                {tab.children}
                {tab.count && tab.count > 0 ? (
                  <span
                    css={css`
                      font-size: 10px;
                    `}
                  >
                    {" "}
                    ({tab.count})
                  </span>
                ) : (
                  ""
                )}
              </NavLink>
            </ConditionalWrapper>
          </React.Fragment>
        )
      })}
    </div>
  )
}

interface IProps2<ITabs> {
  tabs: {
    children: React.ReactNode
    icon: string | IconDefinition
    tabKey: ITabs
    disabled?: string | boolean
    exact?: boolean
    count?: number
    color?: string
    group?: string
  }[]
  selectedTab: ITabs
  onTabChange: (tab: ITabs) => void
}
export function ControlledPageTabs<ITabs extends string>({
  tabs,
  selectedTab,
  onTabChange,
}: IProps2<ITabs>) {
  const theme = useTheme()
  return (
    <div
      css={css`
        display: flex;
        flex-wrap: wrap;
        padding: 0 0 0;
        border-bottom: 1px solid #ddd;
      `}
    >
      {tabs.map((tab, i) => {
        const isNewGroup = i > 0 && tab.group !== undefined && tabs[i - 1].group !== tab.group
        return (
          <React.Fragment key={i}>
            {isNewGroup && <div css={groupSeparator} />}
            <ConditionalWrapper
              condition={typeof tab.disabled === "string"}
              Wrapper={({ children }) => (
                <ToolTip hoverContent={() => tab.disabled}>{children}</ToolTip>
              )}
            >
              <span
                className={selectedTab === tab.tabKey ? "active" : ""}
                css={css`
                padding: 7px 10px;
                @media (pointer: coarse) {
                  padding: 11px 10px;
                }
                display: inline-block;
                text-decoration: none;
                color: #333;
                font-weight: 500;
                text-align: center;
                font-size: 12.5px;
                white-space: nowrap;
                border: 1px solid #ddd;
                background-color: #f5f5f5;
                transition: background-color 0.2s;
                margin: 0 0 -1px -1px;
                position: relative;
                cursor: ${tab.disabled ? "not-allowed" : "pointer"};
                opacity: ${tab.disabled ? 0.5 : 1};
  }
                &.active {
                  &:after {
                    content: "";
                    background: ${tab.color ?? theme.themeColor("primary")};
                    top: 0;
                    width: calc(100% + 2px);
                    z-index: 1;
                    height: 3px;
                    left: 0;
                    margin: -1px;
                    position: absolute;
                  }
                  position: relative;
                  background: white;
                  border-bottom: 1px solid #f5f5f5;
                    &:hover {
                      @media (hover: hover) {
                        background-color: ${tab.disabled ? "" : "white"};
                      }
                    }
                  }
                  &:hover {
                    @media (hover: hover) {
                      background-color: ${tab.disabled ? "" : "#f9f9f9"};
                    }
                  }
                `}
                onClick={e => {
                  if (tab.disabled) {
                    e.preventDefault()
                  } else {
                    onTabChange(tab.tabKey)
                  }
                }}
              >
                <Icon icon={tab.icon} color="#999" /> <Spacer size={4} horizontal />
                {tab.children}
                {tab.count && tab.count > 0 ? (
                  <span
                    css={css`
                      font-size: 10px;
                    `}
                  >
                    {" "}
                    ({tab.count})
                  </span>
                ) : (
                  ""
                )}
              </span>
            </ConditionalWrapper>
          </React.Fragment>
        )
      })}
    </div>
  )
}
